import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Flex, Grid, Modal, Select, Skeleton, Text, TextInput } from '@mantine/core';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useGetTaxonomyCodeOptionsQuery } from '@/store/slices/api/npiApiSlice.ts';
import { usStateOptions } from '@/utils/constants';

import { RegisteredNpi } from '@/types/npi.ts';

import ReferringPhysicianTable from './ReferringProviderTable.tsx';

import '../PatientEditDetailsModal.module.scss';

const formSchema = yup
  .object({
    providerFirstName: yup
      .string()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(2, 'Minimum 2 characters required.')
      .nullable(),
    providerLastName: yup
      .string()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(2, 'Minimum 2 characters required.')
      .nullable(),
    npiNumber: yup
      .number()
      .typeError('NPI must be a number.')
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .test('length-check', 'NPI number must be exactly 10 digits.', function (value) {
        if (value === null) return true;
        const valueAsString = String(value);
        return valueAsString.length === 10;
      })
      .nullable(),
    city: yup
      .string()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(2, 'Minimum 2 characters required.')
      .nullable(),
    state: yup.string().nullable(),
    taxonomy: yup.string().nullable()
  })
  .test('at-least-one-field', 'At least one field must be filled and valid.', function (value) {
    const fields = [
      value?.providerFirstName,
      value?.providerLastName,
      value?.npiNumber,
      value?.city,
      value?.state,
      value?.taxonomy
    ];

    // Check if at least one field is not null, undefined, or empty
    return fields.some((field) => field != null && field !== '');
  });

export type SearchReferringPhysicianModalType = yup.InferType<typeof formSchema>;

interface ReferringProviderSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  onProviderSelect: (provider: RegisteredNpi) => void;
  headingText?: string;
}

const ReferringProviderSelectionModal = ({
  opened,
  onClose,
  onProviderSelect,
  headingText = 'Select Referring Provider'
}: ReferringProviderSelectionModalProps) => {
  const { isLoading: isTaxonomyCodesLoading, data: taxonomyCodeOptions } =
    useGetTaxonomyCodeOptionsQuery();

  const [searchParams, setSearchParams] = useState<SearchReferringPhysicianModalType | null>(null);

  const defaultValues = {
    providerFirstName: '',
    providerLastName: '',
    npiNumber: null,
    city: '',
    state: null,
    taxonomy: null
  };

  const formMethods = useForm<SearchReferringPhysicianModalType>({
    mode: 'onSubmit',
    defaultValues,
    resolver: yupResolver(formSchema)
  });

  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors, isValid }
  } = formMethods;

  const closeModal = () => {
    reset();
    setSearchParams(null);
    onClose();
  };

  const onSearch = (data: SearchReferringPhysicianModalType) => {
    setSearchParams(data);
  };

  const handleProviderUpdate = (newReferringProvider: RegisteredNpi) => {
    onProviderSelect(newReferringProvider);
    closeModal();
  };

  return (
    <Modal.Root opened={opened} onClose={closeModal} size={'75rem'}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Flex gap={'xs'} align={'center'}>
            <Modal.Title fw={700} c={'gray.8'} fz={'h4'}>
              {headingText}
            </Modal.Title>
          </Flex>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <FormProvider {...formMethods}>
            <form
              onSubmit={handleSubmit((data) => {
                onSearch(data);
              })}>
              <Grid mt={'xs'} mb={'sm'}>
                <Grid.Col span={4}>
                  <Text size={'xs'} c={'dimmed'} tt={'uppercase'} fw={600} mb={'2px'}>
                    Provider First Name
                  </Text>
                  <TextInput
                    {...register('providerFirstName')}
                    placeholder="Ex: John"
                    inputMode={'text'}
                    error={errors?.providerFirstName?.message}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text size={'xs'} c={'dimmed'} tt={'uppercase'} fw={600} mb={'2px'}>
                    Provider Last Name
                  </Text>
                  <TextInput
                    {...register('providerLastName')}
                    placeholder="Ex: Doe"
                    inputMode={'text'}
                    error={errors?.providerLastName?.message}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text size={'xs'} c={'dimmed'} tt={'uppercase'} fw={600} mb={'2px'}>
                    NPI Number
                  </Text>
                  <TextInput
                    {...register('npiNumber')}
                    placeholder="Ex: 1234567890"
                    inputMode={'numeric'}
                    error={errors?.npiNumber?.message}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text size={'xs'} c={'dimmed'} tt={'uppercase'} fw={600} mb={'2px'}>
                    City
                  </Text>
                  <TextInput
                    {...register('city')}
                    placeholder="Ex: Baltimore"
                    inputMode={'text'}
                    error={errors?.city?.message}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text size={'xs'} c={'dimmed'} tt={'uppercase'} fw={600} mb={'2px'}>
                    State
                  </Text>
                  <Controller
                    name={'state'}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        placeholder="Ex: Maryland"
                        data={usStateOptions}
                        value={value}
                        clearable
                        searchable
                        onChange={onChange}
                        error={errors?.state?.message}
                      />
                    )}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text size={'xs'} c={'dimmed'} tt={'uppercase'} fw={600} mb={'2px'}>
                    Taxonomy
                  </Text>
                  {isTaxonomyCodesLoading ? (
                    <Skeleton height={'32px'} />
                  ) : (
                    <Controller
                      name={'taxonomy'}
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          placeholder="Select Taxonomy"
                          data={taxonomyCodeOptions}
                          value={value}
                          clearable
                          searchable
                          onChange={onChange}
                          error={errors?.taxonomy?.message}
                        />
                      )}
                    />
                  )}
                </Grid.Col>
              </Grid>
              <Flex gap={'sm'} mt={'md'} justify={'flex-end'}>
                <Button variant={'outline'} onClick={closeModal}>
                  Cancel
                </Button>
                <Button type={'submit'} disabled={!isValid}>
                  Search
                </Button>
              </Flex>
            </form>
          </FormProvider>

          <ReferringPhysicianTable
            searchParams={searchParams}
            handleUpdateReferringProvider={handleProviderUpdate}
          />
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

export default ReferringProviderSelectionModal;
